// stores/websiteContent.ts
//
// API Integrations v4 mapping (see docs/v4-migration/API-COVERAGE.md):
//   GET WebsiteContent/AskedQuestions -> WebsiteContentController.AskedQuestionsAsync
//   GET WebsiteContent/ClientOpinions -> WebsiteContentController.ClientOpinionsAsync
//   GET WebsiteContent/Blogs          -> WebsiteContentController.BlogsAsync
//
// This controller does not exist in v2 at all - it is new in v4. Before this
// migration, the FAQ page (views/faq.vue) rendered six hardcoded questions
// with the literal placeholder text "محتوى الجواب هنا." for every answer,
// and the testimonials partial (views/partial/testimonials.vue) rendered
// three hardcoded reviews attributed to the same fictional person
// ("مشعل محمد") repeated three times. Neither ever called an API - a real,
// pre-existing "page that should call an API and does not" defect (mission
// Phase 8), not something the v2/v4 migration itself introduced. Since v4
// now exposes exactly this content, both are wired to it here rather than
// left as static placeholder copy. Blogs is exposed for completeness/future
// use - there is no blog page or route anywhere in this app today, so it is
// intentionally not wired to any view (building a new blog page/route is a
// feature addition beyond "fix the broken integration," out of this
// mission's scope per Phase 48 - no unnecessary redesign/new UX).
import { defineStore } from "pinia";
import { useBaseStore } from "./base/index";
import { ref } from "vue";

/** Matches WebsiteFaqDto (Helper/Core/Integrations/v4/WebsiteContentModels.cs). */
export interface WebsiteFaq {
  askedQuestionId: number;
  question: string;
  answer: string;
  sortOrder: number;
}

/** Matches WebsiteClientOpinionDto. */
export interface WebsiteClientOpinion {
  clientOpinionId: number;
  clientName: string;
  opinion: string;
  rating: number;
  image: string | null;
  sortOrder: number;
}

/** Matches WebsiteBlogPostDto. Not currently rendered by any page. */
export interface WebsiteBlogPost {
  blogPostId: number;
  title: string;
  image: string | null;
  summary: string;
  content: string;
  createdAt: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaKeywords: string | null;
  metaRobots: string | null;
}

export const useWebsiteContentStore = defineStore("WebsiteContent", () => {
  const base = useBaseStore();
  base.ApiEndPoint("WebsiteContent");

  const faqs = ref<WebsiteFaq[]>([]);
  const faqsLoaded = ref(false);

  const clientOpinions = ref<WebsiteClientOpinion[]>([]);
  const clientOpinionsLoaded = ref(false);

  const getAskedQuestions = async (): Promise<void> => {
    const result = await base.get<WebsiteFaq[]>("AskedQuestions");
    if (result.isValid && result.model) {
      faqs.value = [...result.model].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    faqsLoaded.value = true;
  };

  const getClientOpinions = async (): Promise<void> => {
    const result = await base.get<WebsiteClientOpinion[]>("ClientOpinions");
    if (result.isValid && result.model) {
      clientOpinions.value = [...result.model].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    clientOpinionsLoaded.value = true;
  };

  return {
    ...base,
    faqs,
    faqsLoaded,
    getAskedQuestions,
    clientOpinions,
    clientOpinionsLoaded,
    getClientOpinions,
  };
});
